import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import axios from 'axios';
import { Calendar, ArrowLeft, Share2, Facebook, Twitter, Linkedin, Link as LinkIcon } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogPost() {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBlog();
  }, [id]);

  const loadBlog = async () => {
    try {
      const response = await axios.get(`${API}/blogs/${id}`);
      setBlog(response.data);
    } catch (error) {
      console.error('Failed to load blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCurrentUrl = () => {
    return window.location.href;
  };

  const shareToFacebook = () => {
    const url = getCurrentUrl();
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
  };

  const shareToTwitter = () => {
    const url = getCurrentUrl();
    const text = blog.title;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
  };

  const shareToLinkedIn = () => {
    const url = getCurrentUrl();
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`, '_blank', 'width=600,height=400');
  };

  const copyLink = () => {
    const url = getCurrentUrl();
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center" data-testid="loading-state">
          <div className="w-16 h-16 border-4 border-[#D4A574] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!blog) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center" data-testid="not-found">
          <div className="text-center cassette-card p-12">
            <h2 className="text-3xl retro-title text-[#3D2817] mb-4">Blog Post Not Found</h2>
            <Link to="/student-blog" className="retro-button">
              Back to Blog
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Open Graph & Twitter Card Meta Tags for Social Sharing */}
      <Helmet>
        <title>{blog.title} | Blog</title>
        <meta name="description" content={blog.excerpt} />
        
        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.excerpt} />
        <meta property="og:url" content={getCurrentUrl()} />
        {blog.image_url && <meta property="og:image" content={blog.image_url} />}
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={blog.title} />
        <meta name="twitter:description" content={blog.excerpt} />
        {blog.image_url && <meta name="twitter:image" content={blog.image_url} />}
      </Helmet>

      <article className="py-16 min-h-screen" data-testid="blog-post-page">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/student-blog" className="inline-flex items-center text-black hover:text-gray-600 mb-8 font-semibold" data-testid="back-link">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back to Blog
          </Link>

          {blog.video_url && (
            <div className="video-card mb-8" data-testid="blog-video">
              <div className="aspect-video">
                <iframe
                  src={blog.video_url.includes('youtube.com') || blog.video_url.includes('youtu.be') 
                    ? blog.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')
                    : blog.video_url}
                  title={blog.title}
                  className="w-full h-full rounded-lg"
                  allowFullScreen
                />
              </div>
            </div>
          )}

          {blog.image_url && !blog.video_url && (
            <div className="mb-8" data-testid="blog-hero-image">
              <img
                src={blog.image_url}
                alt={blog.title}
                className="w-full h-96 object-cover rounded-lg border-4 border-[#E8934D] shadow-2xl"
              />
            </div>
          )}

          <div className="admin-card">
            <div className="flex items-center space-x-2 text-[#D4A574] mb-4">
              <Calendar className="w-5 h-5" />
              <span data-testid="blog-date">{formatDate(blog.created_at)}</span>
            </div>

            <h1 className="text-4xl md:text-6xl retro-title mb-6" data-testid="blog-title">{blog.title}</h1>

            {/* Social Share Buttons */}
            <div className="flex items-center gap-3 mb-8 pb-6 border-b-2 border-gray-200">
              <span className="text-gray-700 font-semibold flex items-center gap-2">
                <Share2 className="w-5 h-5" />
                Share:
              </span>
              <button
                onClick={shareToFacebook}
                className="flex items-center gap-2 px-4 py-2 bg-[#1877F2] hover:bg-[#166FE5] text-white rounded-lg transition-colors"
                title="Share on Facebook"
              >
                <Facebook className="w-4 h-4" />
                <span className="hidden sm:inline">Facebook</span>
              </button>
              <button
                onClick={shareToTwitter}
                className="flex items-center gap-2 px-4 py-2 bg-[#1DA1F2] hover:bg-[#1A91DA] text-white rounded-lg transition-colors"
                title="Share on Twitter"
              >
                <Twitter className="w-4 h-4" />
                <span className="hidden sm:inline">Twitter</span>
              </button>
              <button
                onClick={shareToLinkedIn}
                className="flex items-center gap-2 px-4 py-2 bg-[#0A66C2] hover:bg-[#095196] text-white rounded-lg transition-colors"
                title="Share on LinkedIn"
              >
                <Linkedin className="w-4 h-4" />
                <span className="hidden sm:inline">LinkedIn</span>
              </button>
              <button
                onClick={copyLink}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
                title="Copy link"
              >
                <LinkIcon className="w-4 h-4" />
                <span className="hidden sm:inline">Copy Link</span>
              </button>
            </div>

            <div className="prose prose-invert max-w-none" data-testid="blog-content">
              <div className="text-[#F4E4C1] text-xl leading-loose">
                {blog.content.split('\n').map((paragraph, idx) => {
                  // Check if paragraph contains image markdown
                  const imageMatch = paragraph.match(/!\[.*?\]\((.*?)\)/);
                  if (imageMatch) {
                    return (
                      <div key={idx} className="my-8">
                        <img 
                          src={imageMatch[1]} 
                          alt="Blog content" 
                          className="w-full rounded-lg border-4 border-[#E8934D] shadow-2xl"
                        />
                      </div>
                    );
                  }
                  // Regular paragraph
                  if (paragraph.trim()) {
                    return <p key={idx} className="mb-6">{paragraph}</p>;
                  }
                  return null;
                })}
              </div>
            </div>
          </div>
        </div>
      </article>
    </Layout>
  );
}